import { useState, useRef, useCallback, useEffect } from 'react'

const POPUP_HIDE_DELAY = 800

const useDotsPopup = () => {
  const [popupData, setPopupData] = useState(null)
  const popupActiveRef = useRef(false)
  const timerRef = useRef(null)

  const hidePopup = useCallback(() => {
    popupActiveRef.current = false
    setPopupData(null)
  }, [])

  // Two leaves in a row -- one mark to the next, or off a mark and then off its popup -- would
  // otherwise leave the first timer running after the second replaced it, and the unmount below
  // clears only the one it holds: the orphan then hid the popup of a chart already gone.
  const scheduleHide = useCallback(hide => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(hide, POPUP_HIDE_DELAY)
  }, [])

  const handleDotMouseOver = useCallback(({ group, cx, cy, comments }) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setPopupData({
      group,
      coords: [parseFloat(cx), parseFloat(cy)],
      comments: comments || []
    })
  }, [])

  const handleDotMouseOut = useCallback(() => {
    scheduleHide(() => {
      if (popupActiveRef.current) return
      timerRef.current = null

      hidePopup()
    })
  }, [hidePopup, scheduleHide])

  const handleDotsPopupMouseEnter = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    popupActiveRef.current = true
  }, [])

  const handleDotsPopupMouseLeave = useCallback(() => {
    popupActiveRef.current = false

    scheduleHide(() => {
      timerRef.current = null
      hidePopup()
    })
  }, [hidePopup, scheduleHide])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return {
    dotsPopupData: popupData,
    handleDotMouseOver,
    handleDotMouseOut,
    handleDotsPopupMouseEnter,
    handleDotsPopupMouseLeave
  }
}

export default useDotsPopup
